// Voxel ray traversal (Amanatides & Woo). Walks the grid cell-by-cell from an
// origin along a direction, returning the first solid/selectable block hit.

import { Block } from './blocks';
import { REACH } from './constants';
import { Vec3 } from './math';
import { World } from './world';

export interface RayHit {
  /** Coordinates of the block that was hit. */
  block: Vec3;
  /** The empty cell adjacent to the hit face (where a new block would go). */
  place: Vec3;
  /** Unit face normal of the hit face. */
  normal: Vec3;
}

/** Which blocks stop a ray. Water is passed through. */
function selectable(b: Block): boolean {
  return b !== Block.Air && b !== Block.Water;
}

export function raycast(world: World, origin: Vec3, dir: Vec3, maxDist = REACH): RayHit | null {
  let x = Math.floor(origin.x);
  let y = Math.floor(origin.y);
  let z = Math.floor(origin.z);

  const stepX = Math.sign(dir.x);
  const stepY = Math.sign(dir.y);
  const stepZ = Math.sign(dir.z);

  // Distance (in t) to cross one cell along each axis.
  const tDeltaX = dir.x !== 0 ? Math.abs(1 / dir.x) : Infinity;
  const tDeltaY = dir.y !== 0 ? Math.abs(1 / dir.y) : Infinity;
  const tDeltaZ = dir.z !== 0 ? Math.abs(1 / dir.z) : Infinity;

  // Distance (in t) to the first cell boundary along each axis.
  const distToBoundary = (o: number, s: number): number => {
    if (s > 0) return Math.ceil(o) - o;
    if (s < 0) return o - Math.floor(o);
    return Infinity;
  };
  let tMaxX = dir.x !== 0 ? distToBoundary(origin.x, stepX) * tDeltaX : Infinity;
  let tMaxY = dir.y !== 0 ? distToBoundary(origin.y, stepY) * tDeltaY : Infinity;
  let tMaxZ = dir.z !== 0 ? distToBoundary(origin.z, stepZ) * tDeltaZ : Infinity;

  const normal: Vec3 = { x: 0, y: 0, z: 0 };
  let t = 0;

  while (t <= maxDist) {
    if (selectable(world.getBlock(x, y, z))) {
      return {
        block: { x, y, z },
        place: { x: x + normal.x, y: y + normal.y, z: z + normal.z },
        normal: { ...normal },
      };
    }

    if (tMaxX < tMaxY && tMaxX < tMaxZ) {
      x += stepX;
      t = tMaxX;
      tMaxX += tDeltaX;
      normal.x = -stepX;
      normal.y = 0;
      normal.z = 0;
    } else if (tMaxY < tMaxZ) {
      y += stepY;
      t = tMaxY;
      tMaxY += tDeltaY;
      normal.x = 0;
      normal.y = -stepY;
      normal.z = 0;
    } else {
      z += stepZ;
      t = tMaxZ;
      tMaxZ += tDeltaZ;
      normal.x = 0;
      normal.y = 0;
      normal.z = -stepZ;
    }
  }
  return null;
}
